/**
 * @ipc/core · src/status.ts
 *
 * Нейтральный статус платежа — единый набор для всех провайдеров.
 * Хранится в БД (Payment.status, см. tech-spec/02-data-model.md), используется в API/UI/аналитике.
 * Сырые статусы провайдера (напр. MulticardRawStatus) живут ТОЛЬКО внутри драйвера
 * (tech-spec/04-multicard-driver.md) и маппятся сюда функцией вида mapMulticardStatus.
 * Канон — tech-spec/03-core-orchestrator.md, _review/DECISIONS-canon.md.
 */

export const PaymentStatus = {
  CREATED: 'CREATED',
  PENDING: 'PENDING',
  AUTHORIZED: 'AUTHORIZED',
  CONFIRMED: 'CONFIRMED',
  FAILED: 'FAILED',
  REFUNDED: 'REFUNDED',
  PARTIALLY_REFUNDED: 'PARTIALLY_REFUNDED',
  CANCELLED: 'CANCELLED',
} as const;

export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

/** Все значения PaymentStatus, для итерации/валидации. */
export const PAYMENT_STATUSES: readonly PaymentStatus[] = [
  PaymentStatus.CREATED,
  PaymentStatus.PENDING,
  PaymentStatus.AUTHORIZED,
  PaymentStatus.CONFIRMED,
  PaymentStatus.FAILED,
  PaymentStatus.REFUNDED,
  PaymentStatus.PARTIALLY_REFUNDED,
  PaymentStatus.CANCELLED,
];

/**
 * Конечные статусы для no-op-гейта applyProviderStatus (входящие вебхук/callback/polling).
 * PARTIALLY_REFUNDED сюда НЕ входит: он неконечен и допускает доборы возврата
 * (PARTIALLY_REFUNDED → REFUNDED / очередной PARTIALLY_REFUNDED) через отдельные
 * команды оркестратора (refund/partialRefund), а не через произвольный вебхук.
 * CONFIRMED конечен для вебхука/polling, но допускает выход в возвраты через те же
 * явные команды. FAILED/REFUNDED/CANCELLED — тупики без исключений.
 */
export const TERMINAL_STATUSES: readonly PaymentStatus[] = [
  PaymentStatus.CONFIRMED,
  PaymentStatus.FAILED,
  PaymentStatus.REFUNDED,
  PaymentStatus.CANCELLED,
];

export function isTerminalStatus(status: PaymentStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

/** Код провайдера. На старте — только MULTICARD; Payme/Click/CloudPayments — позже. */
export const ProviderCode = {
  MULTICARD: 'multicard',
} as const;
export type ProviderCode = (typeof ProviderCode)[keyof typeof ProviderCode];
